/**
 * CampanasPage — Lista de campañas push (ruta /notificaciones, §8.3.1).
 *
 * KPI cards + tabs + buscador + tabla con acciones por estado.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderBy } from 'firebase/firestore';
import {
  Archive,
  ArchiveRestore,
  BarChart3,
  CalendarClock,
  Copy,
  FlaskConical,
  Loader2,
  Megaphone,
  MousePointerClick,
  Pencil,
  Plus,
  Repeat,
  Search,
  Send,
  Smartphone,
  Trash2,
  XCircle,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useCollection } from '../../hooks/useFirestore';
import { useToast } from '../../contexts/ToastContext';
import {
  archivarCampanaFn,
  cancelarCampanaFn,
  contarDispositivos,
  duplicarCampanaFn,
  eliminarCampanaFn,
  enviarCampanaFn,
  mensajeError,
} from './notificacionesApi';
import {
  ESTADO_BADGE,
  ESTADO_LABELS,
  fmtFechaHora,
  pctSeguro,
  type Campana,
  type EstadoCampana,
} from './tiposCampana';
import { EnviarPruebaModal } from './EnviarPruebaModal';
import styles from './CampanasPage.module.css';

type TabKey = 'todas' | 'borradores' | 'programadas' | 'recurrentes' | 'enviadas' | 'archivadas';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'todas', label: 'Todas' },
  { key: 'borradores', label: 'Borradores' },
  { key: 'programadas', label: 'Programadas' },
  { key: 'recurrentes', label: 'Recurrentes' },
  { key: 'enviadas', label: 'Enviadas' },
  { key: 'archivadas', label: 'Archivadas' },
];

const ESTADOS_POR_TAB: Record<Exclude<TabKey, 'todas' | 'archivadas'>, EstadoCampana[]> = {
  borradores: ['borrador'],
  programadas: ['programada'],
  recurrentes: ['recurrente'],
  enviadas: ['enviada', 'enviando', 'error'],
};

/** ¿Se puede eliminar definitivamente? Solo si nunca tuvo envíos (§ análisis). */
function esEliminable(c: Campana): boolean {
  if (!['borrador', 'cancelada'].includes(c.estado)) return false;
  return (c.stats?.dispositivosObjetivo ?? 0) === 0
    && (c.stats?.aceptadosFcm ?? 0) === 0
    && (c.recurrencia?.enviosRealizados ?? 0) === 0;
}

/** ¿Se puede archivar? Solo estados terminales. */
function esArchivable(c: Campana): boolean {
  return !c.archivada && ['enviada', 'cancelada', 'error'].includes(c.estado);
}

/** Fecha más relevante de la campaña según su estado. */
function fechaRelevante(c: Campana): { label: string; valor: string } {
  if (c.estado === 'programada' && c.programadaPara) {
    return { label: 'Programada para', valor: fmtFechaHora(c.programadaPara) };
  }
  if (c.estado === 'recurrente' && c.recurrencia?.proximaEjecucion) {
    return { label: 'Próximo envío', valor: fmtFechaHora(c.recurrencia.proximaEjecucion) };
  }
  if ((c.estado === 'enviada' || c.estado === 'enviando') && c.enviadaEn) {
    return { label: 'Enviada', valor: fmtFechaHora(c.enviadaEn) };
  }
  return { label: 'Creada', valor: fmtFechaHora(c.creadoEn) };
}

export default function CampanasPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const constraints = useMemo(() => [orderBy('creadoEn', 'desc')], []);
  const { data: campanasRaw, loading } = useCollection<Campana>('campanas', constraints);
  const campanas = useMemo(() => campanasRaw ?? [], [campanasRaw]);

  const [tab, setTab] = useState<TabKey>('todas');
  const [search, setSearch] = useState('');
  const [dispositivos, setDispositivos] = useState<number | null>(null);
  const [confirmEnviar, setConfirmEnviar] = useState<Campana | null>(null);
  const [confirmCancelar, setConfirmCancelar] = useState<Campana | null>(null);
  const [confirmEliminar, setConfirmEliminar] = useState<Campana | null>(null);
  const [pruebaTarget, setPruebaTarget] = useState<Campana | null>(null);
  const [accionEnCurso, setAccionEnCurso] = useState<string | null>(null);

  useEffect(() => {
    let activo = true;
    contarDispositivos()
      .then((total) => {
        if (activo) setDispositivos(total);
      })
      .catch(() => {
        if (activo) setDispositivos(null);
      });
    return () => {
      activo = false;
    };
  }, []);

  // ─── KPIs ──────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const ahora = new Date();
    const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1).getTime();
    const enviadas = campanas.filter((c) => c.estado === 'enviada');
    const enviadasMes = enviadas.filter(
      (c) => (c.enviadaEn?.toMillis?.() ?? 0) >= inicioMes,
    ).length;

    const conAceptados = enviadas.filter((c) => (c.stats?.aceptadosFcm ?? 0) > 0);
    const aperturaProm =
      conAceptados.length > 0
        ? Math.round(
            (conAceptados.reduce(
              (acc, c) => acc + (c.stats?.aperturas ?? 0) / (c.stats?.aceptadosFcm ?? 1),
              0,
            ) /
              conAceptados.length) *
              100,
          )
        : null;

    const pendientes = campanas.filter(
      (c) => c.estado === 'programada' || c.estado === 'recurrente',
    ).length;

    return { enviadasMes, aperturaProm, pendientes };
  }, [campanas]);

  // ─── Filtrado client-side ──────────────────────────────────
  const filtradas = useMemo(() => {
    // Las archivadas viven en su propia pestaña; el resto solo muestra activas
    const activas = campanas.filter((c) => !c.archivada);
    const porTab =
      tab === 'archivadas'
        ? campanas.filter((c) => c.archivada)
        : tab === 'todas'
          ? activas
          : activas.filter((c) => ESTADOS_POR_TAB[tab].includes(c.estado));
    const q = search.trim().toLowerCase();
    if (!q) return porTab;
    return porTab.filter(
      (c) =>
        c.nombre?.toLowerCase().includes(q) ||
        c.titulo?.toLowerCase().includes(q) ||
        c.descripcion?.toLowerCase().includes(q),
    );
  }, [campanas, tab, search]);

  const counts = useMemo(() => {
    const activas = campanas.filter((c) => !c.archivada);
    const porEstado = (estados: EstadoCampana[]) =>
      activas.filter((c) => estados.includes(c.estado)).length;
    return {
      todas: activas.length,
      borradores: porEstado(ESTADOS_POR_TAB.borradores),
      programadas: porEstado(ESTADOS_POR_TAB.programadas),
      recurrentes: porEstado(ESTADOS_POR_TAB.recurrentes),
      enviadas: porEstado(ESTADOS_POR_TAB.enviadas),
      archivadas: campanas.length - activas.length,
    };
  }, [campanas]);

  // ─── Acciones ──────────────────────────────────────────────
  const ejecutarEnvio = async (c: Campana) => {
    setAccionEnCurso(c.id);
    try {
      await enviarCampanaFn({ campanaId: c.id });
      addToast(
        'success',
        'Envío iniciado. El progreso se actualiza en vivo en el detalle de la campaña.',
      );
      navigate(`/notificaciones/${c.id}`);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo enviar la campaña.'));
    } finally {
      setAccionEnCurso(null);
      setConfirmEnviar(null);
    }
  };

  const ejecutarCancelacion = async (c: Campana) => {
    setAccionEnCurso(c.id);
    try {
      await cancelarCampanaFn({ campanaId: c.id });
      addToast('success', `Campaña "${c.nombre}" cancelada.`);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo cancelar la campaña.'));
    } finally {
      setAccionEnCurso(null);
      setConfirmCancelar(null);
    }
  };

  const ejecutarEliminacion = async (c: Campana) => {
    setAccionEnCurso(c.id);
    try {
      await eliminarCampanaFn({ campanaId: c.id });
      addToast('success', `Campaña "${c.nombre}" eliminada definitivamente.`);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo eliminar la campaña.'));
    } finally {
      setAccionEnCurso(null);
      setConfirmEliminar(null);
    }
  };

  const alternarArchivo = async (c: Campana) => {
    setAccionEnCurso(c.id);
    const archivar = !c.archivada;
    try {
      await archivarCampanaFn({ campanaId: c.id, archivar });
      addToast('success', archivar
        ? `Campaña "${c.nombre}" archivada: ya no es visible en la app móvil. La encuentra en la pestaña Archivadas.`
        : `Campaña "${c.nombre}" restaurada${['enviada', 'recurrente'].includes(c.estado) ? ' y visible de nuevo en la app (si sigue vigente)' : ''}.`);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo archivar la campaña.'));
    } finally {
      setAccionEnCurso(null);
    }
  };

  const duplicar = async (c: Campana) => {
    setAccionEnCurso(c.id);
    try {
      const res = await duplicarCampanaFn({ campanaId: c.id });
      addToast('success', `Campaña duplicada como borrador.`);
      navigate(`/notificaciones/${res.data.campanaId}/editar`);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo duplicar la campaña.'));
    } finally {
      setAccionEnCurso(null);
    }
  };

  const badgeEstado = (estado: EstadoCampana) => (
    <Badge variant={ESTADO_BADGE[estado]} size="sm">
      <span className={styles.badgeInner}>
        {estado === 'recurrente' && <Repeat size={11} aria-hidden="true" />}
        {estado === 'enviando' && (
          <Loader2 size={11} className={styles.spin} aria-hidden="true" />
        )}
        {ESTADO_LABELS[estado]}
      </span>
    </Badge>
  );

  return (
    <MainLayout
      pageTitle="Campañas de notificaciones"
      pageSubtitle="Cree, programe y mida campañas push"
    >
      <div className={styles.page}>
        {/* ─── KPI cards ─── */}
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>Enviadas este mes</p>
              <span className={`${styles.kpiIcon} ${styles.kpiIconPrimary}`}>
                <Megaphone size={16} aria-hidden="true" />
              </span>
            </div>
            <p className={styles.kpiValue}>{loading ? '…' : kpis.enviadasMes}</p>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>Dispositivos registrados</p>
              <span className={`${styles.kpiIcon} ${styles.kpiIconInfo}`}>
                <Smartphone size={16} aria-hidden="true" />
              </span>
            </div>
            <p className={styles.kpiValue}>
              {dispositivos === null ? '…' : dispositivos.toLocaleString('es-PA')}
            </p>
            <p className={styles.kpiMeta}>Audiencia estimada de la app</p>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>Apertura promedio</p>
              <span className={`${styles.kpiIcon} ${styles.kpiIconGold}`}>
                <MousePointerClick size={16} aria-hidden="true" />
              </span>
            </div>
            <p className={styles.kpiValue}>
              {loading ? '…' : kpis.aperturaProm === null ? '—' : `${kpis.aperturaProm}%`}
            </p>
            <p className={styles.kpiMeta}>Sobre campañas enviadas</p>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiHeader}>
              <p className={styles.kpiLabel}>Programadas pendientes</p>
              <span className={`${styles.kpiIcon} ${styles.kpiIconWarning}`}>
                <CalendarClock size={16} aria-hidden="true" />
              </span>
            </div>
            <p className={styles.kpiValue}>{loading ? '…' : kpis.pendientes}</p>
            <p className={styles.kpiMeta}>Incluye recurrentes activas</p>
          </div>
        </div>

        {/* ─── Tabs + CTA ─── */}
        <div className={styles.toolbar}>
          <div className={styles.tabs} role="tablist" aria-label="Filtrar por estado">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
                onClick={() => setTab(t.key)}
              >
                <span>{t.label}</span>
                <span className={styles.tabCount}>{counts[t.key]}</span>
              </button>
            ))}
          </div>
          <Button variant="primary" onClick={() => navigate('/notificaciones/nueva')}>
            <Plus size={16} aria-hidden="true" /> Nueva campaña
          </Button>
        </div>

        {/* ─── Buscador ─── */}
        <div className={styles.searchBar}>
          <Input
            placeholder="Buscar por nombre o título…"
            leftIcon={<Search size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar campañas"
          />
        </div>

        {/* ─── Tabla ─── */}
        <div className={styles.tableCard}>
          {loading ? (
            <p className={styles.loading}>
              <Loader2 size={16} className={styles.spin} aria-hidden="true" /> Cargando campañas…
            </p>
          ) : filtradas.length === 0 ? (
            <EmptyState
              variant="search"
              title={search ? 'Sin resultados' : 'Aún no hay campañas'}
              message={
                search
                  ? 'No se encontraron campañas con ese criterio.'
                  : 'Cree su primera campaña push para llegar a los usuarios de la app.'
              }
              action={
                !search ? (
                  <Button variant="primary" onClick={() => navigate('/notificaciones/nueva')}>
                    <Plus size={16} aria-hidden="true" /> Nueva campaña
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Campaña</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Aceptadas / objetivo</th>
                    <th>Aperturas</th>
                    <th className={styles.actionsCol}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((c) => {
                    const fecha = fechaRelevante(c);
                    const stats = c.stats;
                    const editable = ['borrador', 'programada', 'recurrente'].includes(c.estado);
                    const enviable = c.estado === 'borrador' || c.estado === 'programada';
                    const cancelable = c.estado === 'programada' || c.estado === 'recurrente';
                    return (
                      <tr key={c.id}>
                        <td>
                          <button
                            type="button"
                            className={styles.campanaCell}
                            onClick={() => navigate(`/notificaciones/${c.id}`)}
                            title="Ver detalle"
                          >
                            {c.imagenUrl ? (
                              <img
                                src={c.imagenUrl}
                                alt=""
                                className={styles.thumb}
                                loading="lazy"
                              />
                            ) : (
                              <span className={`${styles.thumb} ${styles.thumbPlaceholder}`} aria-hidden="true">
                                <Megaphone size={13} />
                              </span>
                            )}
                            <span className={styles.campanaText}>
                              <span className={styles.campanaNombre}>{c.nombre}</span>
                              <span className={styles.campanaTitulo}>{c.titulo}</span>
                            </span>
                          </button>
                        </td>
                        <td>{badgeEstado(c.estado)}</td>
                        <td>
                          <div className={styles.fechaCell}>
                            <span className={styles.fechaLabel}>{fecha.label}</span>
                            <span className={styles.fechaValor}>{fecha.valor}</span>
                          </div>
                        </td>
                        <td>
                          {stats && stats.dispositivosObjetivo > 0 ? (
                            <span className={styles.embudo}>
                              {stats.aceptadosFcm.toLocaleString('es-PA')} /{' '}
                              {stats.dispositivosObjetivo.toLocaleString('es-PA')}
                            </span>
                          ) : (
                            <span className={styles.sinDatos}>—</span>
                          )}
                        </td>
                        <td>
                          {stats && stats.aceptadosFcm > 0 ? (
                            <span className={styles.embudo}>
                              {pctSeguro(stats.aperturas, stats.aceptadosFcm)}
                            </span>
                          ) : (
                            <span className={styles.sinDatos}>—</span>
                          )}
                        </td>
                        <td>
                          <div className={styles.actions}>
                            {editable && (
                              <button
                                type="button"
                                className={styles.actionBtn}
                                title="Editar campaña"
                                aria-label={`Editar ${c.nombre}`}
                                onClick={() => navigate(`/notificaciones/${c.id}/editar`)}
                              >
                                <Pencil size={14} aria-hidden="true" />
                              </button>
                            )}
                            {['borrador', 'programada', 'recurrente'].includes(c.estado) && (
                              <button
                                type="button"
                                className={styles.actionBtn}
                                title="Enviar solo a usuarios de prueba"
                                aria-label={`Enviar ${c.nombre} a usuarios de prueba`}
                                disabled={accionEnCurso === c.id}
                                onClick={() => setPruebaTarget(c)}
                              >
                                <FlaskConical size={14} aria-hidden="true" />
                              </button>
                            )}
                            {enviable && (
                              <button
                                type="button"
                                className={styles.actionBtn}
                                title="Enviar ahora a todos los dispositivos"
                                aria-label={`Enviar ${c.nombre} ahora`}
                                disabled={accionEnCurso === c.id}
                                onClick={() => setConfirmEnviar(c)}
                              >
                                <Send size={14} aria-hidden="true" />
                              </button>
                            )}
                            <button
                              type="button"
                              className={styles.actionBtn}
                              title="Duplicar campaña"
                              aria-label={`Duplicar ${c.nombre}`}
                              disabled={accionEnCurso === c.id}
                              onClick={() => void duplicar(c)}
                            >
                              <Copy size={14} aria-hidden="true" />
                            </button>
                            {cancelable && (
                              <button
                                type="button"
                                className={styles.actionBtnDanger}
                                title="Cancelar campaña"
                                aria-label={`Cancelar ${c.nombre}`}
                                disabled={accionEnCurso === c.id}
                                onClick={() => setConfirmCancelar(c)}
                              >
                                <XCircle size={14} aria-hidden="true" />
                              </button>
                            )}
                            {esArchivable(c) && (
                              <button
                                type="button"
                                className={styles.actionBtn}
                                title="Archivar (la oculta de la app móvil; métricas intactas)"
                                aria-label={`Archivar ${c.nombre}`}
                                disabled={accionEnCurso === c.id}
                                onClick={() => void alternarArchivo(c)}
                              >
                                <Archive size={14} aria-hidden="true" />
                              </button>
                            )}
                            {c.archivada && (
                              <button
                                type="button"
                                className={styles.actionBtn}
                                title="Restaurar de archivadas"
                                aria-label={`Restaurar ${c.nombre}`}
                                disabled={accionEnCurso === c.id}
                                onClick={() => void alternarArchivo(c)}
                              >
                                <ArchiveRestore size={14} aria-hidden="true" />
                              </button>
                            )}
                            {esEliminable(c) && (
                              <button
                                type="button"
                                className={styles.actionBtnDanger}
                                title="Eliminar definitivamente"
                                aria-label={`Eliminar ${c.nombre}`}
                                disabled={accionEnCurso === c.id}
                                onClick={() => setConfirmEliminar(c)}
                              >
                                <Trash2 size={14} aria-hidden="true" />
                              </button>
                            )}
                            <button
                              type="button"
                              className={styles.actionBtn}
                              title="Ver detalle y métricas"
                              aria-label={`Ver detalle de ${c.nombre}`}
                              onClick={() => navigate(`/notificaciones/${c.id}`)}
                            >
                              <BarChart3 size={14} aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ─── Confirmar envío ─── */}
      <Modal
        open={confirmEnviar !== null}
        onClose={() => setConfirmEnviar(null)}
        title="Enviar campaña ahora"
      >
        {confirmEnviar && (
          <div className={styles.confirmBody}>
            <p className={styles.confirmText}>
              Vas a enviar la campaña <strong>{confirmEnviar.nombre}</strong> a ~
              {dispositivos !== null ? dispositivos.toLocaleString('es-PA') : '…'} dispositivos.
              Esta acción no se puede cancelar una vez iniciada.
            </p>
            <div className={styles.confirmActions}>
              <Button variant="secondary" onClick={() => setConfirmEnviar(null)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                loading={accionEnCurso === confirmEnviar.id}
                onClick={() => void ejecutarEnvio(confirmEnviar)}
              >
                <Send size={14} aria-hidden="true" /> Enviar ahora
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Confirmar cancelación ─── */}
      <Modal
        open={confirmCancelar !== null}
        onClose={() => setConfirmCancelar(null)}
        title="Cancelar campaña"
      >
        {confirmCancelar && (
          <div className={styles.confirmBody}>
            <p className={styles.confirmText}>
              ¿Desea cancelar la campaña <strong>{confirmCancelar.nombre}</strong>?{' '}
              {confirmCancelar.estado === 'recurrente'
                ? 'No se realizarán más envíos recurrentes.'
                : 'No se enviará en la fecha programada.'}
            </p>
            <div className={styles.confirmActions}>
              <Button variant="secondary" onClick={() => setConfirmCancelar(null)}>
                Volver
              </Button>
              <Button
                variant="danger"
                loading={accionEnCurso === confirmCancelar.id}
                onClick={() => void ejecutarCancelacion(confirmCancelar)}
              >
                Sí, cancelar campaña
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Enviar a usuarios de prueba ─── */}
      <EnviarPruebaModal
        open={pruebaTarget !== null}
        onClose={() => setPruebaTarget(null)}
        campanaId={pruebaTarget?.id}
      />

      {/* ─── Confirmar eliminación definitiva ─── */}
      <Modal
        open={confirmEliminar !== null}
        onClose={() => setConfirmEliminar(null)}
        title="Eliminar campaña"
      >
        {confirmEliminar && (
          <div className={styles.confirmBody}>
            <p className={styles.confirmText}>
              ¿Desea eliminar <strong>{confirmEliminar.nombre}</strong> definitivamente?
              Esta acción no se puede deshacer: se borrará la campaña
              {confirmEliminar.imagenUrl ? ' y su imagen' : ''}.
            </p>
            <div className={styles.confirmActions}>
              <Button variant="secondary" onClick={() => setConfirmEliminar(null)}>
                Volver
              </Button>
              <Button
                variant="danger"
                loading={accionEnCurso === confirmEliminar.id}
                onClick={() => void ejecutarEliminacion(confirmEliminar)}
              >
                Sí, eliminar definitivamente
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </MainLayout>
  );
}
